import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Table, Button, Input, Select, Space, Tag, Drawer, message, Typography, Tooltip } from "antd";
import { PlusOutlined, EyeOutlined, EditOutlined } from "@ant-design/icons";
import ViewBanner from "../../components/banners/ViewBanner";
import bannerService from "../../services/bannerService";
import masterService from "../../services/masterService";
import { useCommon } from "../../context/CommonContext";
const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

// The backend splits the term on the surviving spaces to match each word, so a
// space typed after a word would otherwise turn a hit into a miss.
const normalizeSearchTerm = (value) =>
  String(value ?? "").replace(/\s+/g, " ").trim();

const SEARCH_DEBOUNCE_MS = 250;

const BannerList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  useEffect(() => {
    setPageTitle("Banners");
  }, []);
  const [bannerList, setBannerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [viewObject, setViewObject] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  const [filters, setFilters] = useState({
    searchText: "",
    status: undefined,
    banner_type: undefined,
    show_type: undefined,
    page: undefined,
  });

  // Debounced into filters.searchText below so each keystroke is not a request.
  const [searchInput, setSearchInput] = useState("");

  // Responses can land out of order — "bl" can answer after "blood". Every fetch
  // takes a ticket and only the newest may write results.
  const latestRequestRef = useRef(0);

  const bannerColumns = [
    {
      title: "Banner Name",
      dataIndex: "banner_name",
      key: "banner_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.banner_name.localeCompare(b.banner_name),
    },
    {
      title: "Show Type",
      dataIndex: "show_type",
      key: "show_type",
      render: (text) => (
        <Tag color={text === "movie" ? "blue" : "green"}>
          {text === "movie" ? "Movie" : "TV Show"}
        </Tag>
      ),
    },
    {
      title: "Show Name",
      key: "showName",
      render: (text, record) => (
        <Text>
          {record.show_type === "movie"
            ? record.video_show_title
            : record.tv_show_name}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "banner_is_active",
      key: "banner_is_active",
      defaultSortOrder: "ascend",
      render: (text) => (
        <Tag color={text === "Y" ? "green" : "red"}>
          {text === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",

      render: (text, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const getBanners = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = {
        ...filters,
        perPage: pagination.pageSize,
        currentPage: pagination.current,
      };

      const response = await bannerService.getBanners(params);

      // Superseded by a newer search — drop these rather than overwrite it.
      if (requestId !== latestRequestRef.current) return;

      const { data } = response;

      if (data && data.status) {
        setBannerList(data.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data.data.pagination?.total || 0,
          current: data.data.pagination?.currentPage || 1,
        }));
      }
    } catch (error) {
      // A superseded request must not raise an error over results still to land.
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching banners:", error);
      message.error("Failed to fetch banners");
    } finally {
      // Only the newest clears the spinner — an overtaken request finishing early
      // would show the list as loaded while it is still loading.
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (value) => {
    setSearchInput(value);
    setFilters((prev) => ({ ...prev, searchText: normalizeSearchTerm(value) }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Back to page 1: the current page may not exist under the narrower filter.
  const handleFilterChange = (key) => (value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    getBanners();
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setFilters({
      searchText: "",
      status: undefined,
        banner_type: undefined,
      show_type: undefined,
      page: undefined,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    if (
      newPagination.current !== pagination.current ||
      newPagination.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: newPagination.current,
        pageSize: newPagination.pageSize,
      }));
    }
  };

  const handleView = (record) => {
    setViewObject(record);
    setViewVisible(true);
  };

  const handleEdit = (record) => {
    navigate(`/banners/edit/${record.m_banner_id}`);
  };

  const handleAddBanner = () => {
    navigate("/banners/add/new");
  };

  const handleCloseView = () => {
    setViewVisible(false);
    setViewObject(null);
  };

  // Compared normalized on both sides so pressing Enter and then stopping typing
  // settles on the same term instead of firing a second identical request.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = normalizeSearchTerm(searchInput);
      setFilters((prev) =>
        prev.searchText === term ? prev : { ...prev, searchText: term }
      );
      setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getBanners();
  }, [
    pagination.current,
    pagination.pageSize,
    filters.searchText,
    filters.status,
    filters.banner_type,
    filters.show_type,
    filters.page,
  ]);

  return (
    <>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Banners ({pagination.total})
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddBanner}
          >
            Add Banner
          </Button>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          <Select
            placeholder="Status"
            style={{ width: 140 }}
            value={filters.status}
            onChange={handleStatusChange}
            allowClear
          >
            <Option value="Y">Active</Option>
            <Option value="N">Inactive</Option>
          </Select>
          <Search
            placeholder="Search banner name..."
            style={{ width: 260 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={handleSearch}
            allowClear
          />
          <Select
            placeholder="Banner Type"
            style={{ width: 150 }}
            value={filters.banner_type}
            onChange={handleFilterChange("banner_type")}
            allowClear
          >
            <Option value="show">Show</Option>
            <Option value="generic">Generic</Option>
            <Option value="promotion">Promotion</Option>
          </Select>
          <Select
            placeholder="Show Type"
            style={{ width: 140 }}
            value={filters.show_type}
            onChange={handleFilterChange("show_type")}
            allowClear
          >
            <Option value="movie">Movie</Option>
            <Option value="series">Series</Option>
          </Select>
          <Select
            placeholder="Page"
            style={{ width: 130 }}
            value={filters.page}
            onChange={handleFilterChange("page")}
            allowClear
          >
            <Option value="home">Home</Option>
            <Option value="movie">Movie</Option>
            <Option value="series">Series</Option>
          </Select>
          <Space>
            <Button onClick={handleResetFilters}>Reset</Button>
            <Button type="primary" onClick={handleApplyFilters}>
              Apply
            </Button>
          </Space>
        </div>

        <Table
          columns={bannerColumns}
          dataSource={bannerList}
          rowKey="m_banner_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
        />
      </Card>

      <Drawer
        title="Banner Details"
        width={640}
        placement="right"
        closable={false}
        open={viewVisible}
        onClose={handleCloseView}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button type="primary" onClick={handleCloseView}>
              Close
            </Button>
          </div>
        }
      >
        <ViewBanner viewdata={viewObject}>
        </ViewBanner>
      </Drawer>
    </>
  );
};

export default BannerList;
